import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";
import { saveAs } from "file-saver";

interface PrimeCostsReportState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  filter: {
    date_from: any;
    date_to: any;
    products: any[];
  };

  items: any[];
  months: any[];
}

const state: PrimeCostsReportState = {
  loading: false,
  loaded: false,
  fatalError: false,

  filter: {
    date_from: null,
    date_to: null,
    products: []
  },

  items: [],
  months: []
};

function buildQuery(filter: any) {
  return {
    columns: {
      id: "pct.id",
      month: "pc.month",
      cost: "pc.cost",
      product: "p.name",
      product_container: "pct.name"
    },
    from: "s:prime_costs",
    from_as: "pc",
    joins: [
      {
        join: "s:product_containers",
        as: "pct",
        on: {
          $eq: ["pc.product_container_id", "pct.id"]
        }
      },
      {
        join: "s:products",
        as: "p",
        on: {
          $eq: ["pct.product_id", "p.id"]
        }
      }
    ],
    where: {
      $and: [
        ...(filter.date_from
          ? [
              {
                $gte: [
                  "pc.month",
                  {
                    $: filter.date_from
                  }
                ]
              }
            ]
          : []),
        ...(filter.date_to
          ? [
              {
                $lte: [
                  "pc.month",
                  {
                    $: filter.date_to
                  }
                ]
              }
            ]
          : []),
        ...(filter.products && filter.products.length
          ? [
              {
                $in: [
                  "p.id",
                  {
                    $: filter.products
                  }
                ]
              }
            ]
          : [])
      ]
    },
    page_size: 100000
  };
}

function buildExportQuery(filter: any) {
  return {
    template:
      "<!DOCTYPE html>\n<table font=Verdana font-size=8>\n  <tr>\n    <td wrap bgcolor=FF72BF44 width=10>Дата</td>\n    <td wrap bgcolor=FF72BF44 width=45>Клиент</td>\n    <td wrap bgcolor=FF72BF44 width=30>Причина</td>\n    <td wrap bgcolor=FF72BF44 width=150>Описание</td>\n  </tr>\n  {{#clients.documents}}\n    <tr>\n      <td wrap>{{deal_delivery_date}}</td>\n      <td wrap>{{client_name}}</td>\n      <td wrap>{{reason_name}}</td>\n      <td wrap>{{description}}</td>\n    </tr>\n  {{/clients.documents}}\n</table>\n",
    queries: {
      clients: buildQuery(filter)
    },
    format: "xlsx"
  };
}

function buildFilter(params: any) {
  const queryParams: any = {};

  if (params.date_from) {
    queryParams.date_from = moment(params.date_from).format("YYYY-MM-DD");
  }

  if (params.date_to) {
    queryParams.date_to = moment(params.date_to).format("YYYY-MM-DD");
  }

  if (params.products) {
    queryParams.products = Array.isArray(params.products)
      ? params.products
      : [params.products];
  }

  if (params.sortField !== null) {
    queryParams.order_by = params.sortField;
    queryParams.order_type = params.sortDirection;
  }

  return queryParams;
}

export const REPORT_DATA = "REPORT_DATA";

export const actions: ActionTree<PrimeCostsReportState, RootState> = {
  loadData({ commit, state, dispatch }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      if (Object.values(params).length > 0) {
        commit("START_LOADING");

        vm.$db([
          vm
            .$q(REPORT_DATA)
            .raw("documents.list")
            .action(buildQuery(buildFilter(params)))
        ]).then((result: ServerResult) => {
          commit(
            "SET_REPORT_DATA",
            result.getOperation(REPORT_DATA).getContent()
          );
          commit("LOADED_SUCCESS");

          resolve();
        });
      } else {
        resolve();
      }
    });
  },

  exportDataAsXLSX({ rootState }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("documents.report")
          .action(buildExportQuery(buildFilter(params)))
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.xlsx");

        resolve();
      });
    });
  }
};

export const mutations: MutationTree<PrimeCostsReportState> = {
  START_LOADING: state => {
    state.loaded = false;
    state.fatalError = false;
  },
  LOADED_SUCCESS: state => {
    state.loaded = true;
    state.fatalError = false;
  },
  LOADED_ERROR: state => {
    state.loaded = true;
    state.fatalError = true;
  },

  SET_REPORT_DATA(state, data) {
    state.months = [];
    state.items = Object.values(
      data.documents.reduce((acc: any, item: any) => {
        if (!acc[item.id]) {
          acc[item.id] = {
            id: item.id,
            product: item.product,
            product_container: item.product_container,
            months: {}
          };
        }
        if (!state.months[item.month]) {
          state.months[item.month] = {
            name: moment(item.month).format("MM-YYYY"),
            id: item.month
          };
        }
        acc[item.id].months[item.month] = item.cost;
        return acc;
      }, {})
    );
    state.months = Object.values(state.months);
  }
};

const getters = {};

export const STORE_KEY = "$_prime_costs_report";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
